// ---------------------------------------------------------------------------
// TanStack Query hooks for template API
// ---------------------------------------------------------------------------

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type {
  Template,
  TemplateDetail,
  TemplateFilters,
  CreateTemplateRequest,
  UpdateTemplateRequest,
  TemplatePrompt,
  AddPromptRequest,
  UpdatePromptRequest,
  TemplateDataGroup,
  DataGroup,
  AddDataGroupRequest,
} from '@/types/template';
import type { PaginatedResponse } from '@/types/common';

const TEMPLATES_KEY = 'templates';

function buildQueryString(filters: TemplateFilters): string {
  const params = new URLSearchParams();
  if (filters.type) params.set('type', filters.type);
  if (filters.status) params.set('status', filters.status);
  if (filters.customerId) params.set('customerId', String(filters.customerId));
  if (filters.diseaseAssociation) params.set('diseaseAssociation', filters.diseaseAssociation);
  if (filters.keyword) params.set('keyword', filters.keyword);
  // SE-API-18: standard page/pageSize pagination.
  params.set('page', String(filters.page ?? 1));
  params.set('pageSize', String(filters.pageSize ?? 25));
  return params.toString();
}

export function useTemplates(filters: TemplateFilters) {
  return useQuery({
    queryKey: [TEMPLATES_KEY, filters],
    queryFn: () =>
      api.get<PaginatedResponse<Template>>(
        `/templates?${buildQueryString(filters)}`,
      ),
  });
}

export function useTemplate(templateId: string | undefined) {
  return useQuery({
    queryKey: [TEMPLATES_KEY, templateId],
    queryFn: () => api.get<TemplateDetail>(`/template?id=${templateId}`),
    enabled: !!templateId,
  });
}

export function useCreateTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateTemplateRequest) =>
      api.post<Template>('/templates', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TEMPLATES_KEY] });
    },
  });
}

export function useUpdateTemplate(templateId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateTemplateRequest) =>
      api.patch<{ entries: Template[] }>(`/template`, {
        entries: [{ id: templateId, ...data }],
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TEMPLATES_KEY] });
    },
  });
}

export function useDeleteTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (templateId: string) =>
      api.delete(`/template`, { entries: [{ id: templateId }] }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TEMPLATES_KEY] });
    },
  });
}

// -- Prompt hooks (Sprint 2) --

export function useAddPrompt(templateId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: AddPromptRequest) =>
      api
        .post<{ entries: TemplatePrompt[] }>(`/prompt`, {
          entries: [{ templateId, ...data }],
        })
        .then((r) => r.entries[0]),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TEMPLATES_KEY, templateId] });
    },
  });
}

export function useUpdatePrompt(templateId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      promptId,
      ...data
    }: UpdatePromptRequest & { promptId: string }) =>
      api
        .patch<{ entries: TemplatePrompt[] }>(`/prompt`, {
          entries: [{ id: promptId, ...data }],
        })
        .then((r) => r.entries[0]),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TEMPLATES_KEY, templateId] });
    },
  });
}

export function useDeletePrompt(templateId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (promptId: string) =>
      api.delete(`/prompt`, { entries: [{ id: promptId }] }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TEMPLATES_KEY, templateId] });
    },
  });
}

export function useReorderPrompts(templateId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    // SE-API-14: reorder folds into PATCH /prompt — send sort_order + is_root
    // per prompt (the first in the order becomes root).
    mutationFn: (orderedIds: string[]) =>
      api.patch(`/prompt`, {
        entries: orderedIds.map((id, i) => ({
          id,
          sortOrder: i + 1,
          isRoot: i === 0,
        })),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TEMPLATES_KEY, templateId] });
    },
  });
}

// -- Data-group hooks --

export function useDataGroups(templateId: string | undefined) {
  return useQuery({
    queryKey: [TEMPLATES_KEY, templateId, 'data-groups'],
    queryFn: () =>
      api.get<TemplateDataGroup[]>(
        `/template/data-groups?templateId=${templateId}`,
      ),
    enabled: !!templateId,
  });
}

export function useReferenceDataGroups() {
  return useQuery({
    queryKey: ['reference', 'data-groups'],
    queryFn: () => api.get<DataGroup[]>('/reference/data-groups'),
  });
}

export function useAddDataGroup(templateId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: AddDataGroupRequest) =>
      api
        .post<{ entries: TemplateDataGroup[] }>(`/template/data-group`, {
          entries: [{ templateId, ...data }],
        })
        .then((r) => r.entries[0]),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TEMPLATES_KEY, templateId] });
    },
  });
}

export function useRemoveDataGroup(templateId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dataGroupId: string) =>
      api.delete(`/template/data-group`, {
        entries: [{ templateId, dataGroupId }],
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TEMPLATES_KEY, templateId] });
    },
  });
}
